import type { ToolbarItemMode, ToolbarItemPin, ToolbarLayout } from "./types"

/**
 * An item reduced to the numbers the layout algorithm needs. `index` is the
 * item's position in the original list and breaks priority ties so the
 * right-most item overflows first.
 */
export interface MeasuredToolbarItem {
  id: string
  priority: number
  index: number
  pin: ToolbarItemPin
  fullWidth: number
  compactWidth: number
  canCompact: boolean
  canOverflow: boolean
}

function rowWidth(widths: number[], gap: number): number {
  if (widths.length === 0) {
    return 0
  }
  let total = 0
  for (const width of widths) {
    total += width
  }
  return total + gap * (widths.length - 1)
}

function widthInRow(item: MeasuredToolbarItem): number {
  return item.canCompact ? item.compactWidth : item.fullWidth
}

function modeOf(item: MeasuredToolbarItem): ToolbarItemMode {
  return item.canCompact ? "compact" : "full"
}

/**
 * Decides, for a given available width, which items render full, which render
 * compact (icon-only), and which leave the visible row.
 *
 * The cascade tries full labels, compacts all eligible items, then removes overflowable items lowest-priority-first until the row fits, reserving a trigger only in menu mode; pinned items remain even when they exceed the available width.
 *
 * Pure and DOM-free so it can be unit-tested directly.
 */
export function computeLayout(
  items: MeasuredToolbarItem[],
  availableWidth: number,
  gap: number,
  triggerWidth: number,
  overflowBehavior: "menu" | "hide" = "menu",
): ToolbarLayout {
  if (items.length === 0) {
    return { visible: [], overflow: [] }
  }

  const fullRow = rowWidth(
    items.map((item) => item.fullWidth),
    gap,
  )
  if (fullRow <= availableWidth) {
    return {
      visible: items.map((item) => ({ id: item.id, mode: "full" })),
      overflow: [],
    }
  }

  const compactRow = rowWidth(items.map(widthInRow), gap)
  if (compactRow <= availableWidth) {
    return {
      visible: items.map((item) => ({ id: item.id, mode: modeOf(item) })),
      overflow: [],
    }
  }

  const overflowOrder = items
    .filter((item) => item.canOverflow)
    .sort((a, b) => a.priority - b.priority || b.index - a.index)

  const overflowed = new Set<string>()
  for (const candidate of overflowOrder) {
    const visibleWidths = items
      .filter((item) => !overflowed.has(item.id))
      .map(widthInRow)
    const withTrigger = rowWidth(
      overflowBehavior === "hide"
        ? visibleWidths
        : [...visibleWidths, triggerWidth],
      gap,
    )
    if (withTrigger <= availableWidth) {
      break
    }
    overflowed.add(candidate.id)
  }

  if (overflowed.size === 0) {
    return {
      visible: items.map((item) => ({ id: item.id, mode: modeOf(item) })),
      overflow: [],
    }
  }

  return {
    visible: items
      .filter((item) => !overflowed.has(item.id))
      .map((item) => ({ id: item.id, mode: modeOf(item) })),
    overflow: items
      .filter((item) => overflowed.has(item.id))
      .map((item) => item.id),
  }
}

export function layoutsEqual(a: ToolbarLayout, b: ToolbarLayout): boolean {
  if (a.visible.length !== b.visible.length) {
    return false
  }
  if (a.overflow.length !== b.overflow.length) {
    return false
  }
  for (let i = 0; i < a.visible.length; i++) {
    if (a.visible[i].id !== b.visible[i].id) {
      return false
    }
    if (a.visible[i].mode !== b.visible[i].mode) {
      return false
    }
  }
  for (let i = 0; i < a.overflow.length; i++) {
    if (a.overflow[i] !== b.overflow[i]) {
      return false
    }
  }
  return true
}
